import {
  ChainOfThought,
  ChainOfThoughtContent,
  ChainOfThoughtHeader,
  ChainOfThoughtStep,
  MessageResponse,
} from '@newfront-insurance/nova-core';
import { CheckCircle2Icon } from 'lucide-react';
import { memo } from 'react';

import { CoTToolInput, CoTToolOutput, CoTToolSearchResults } from './chain-of-thought-renderers';
import {
  type CoTStep,
  getCoTStepIcon,
  getCoTStepKey,
  getCoTStepLabel,
  getCoTStepStatus,
  stripLeadingTitle,
} from '../lib/chain-of-thought-utils';

interface ChainOfThoughtBlockProps {
  steps: CoTStep[];
  reasoningIsStreaming: boolean;
  stepProgressMap: Record<string, string>;
}

export const ChainOfThoughtBlock = memo(
  ({ steps, reasoningIsStreaming, stepProgressMap }: ChainOfThoughtBlockProps) => (
    <ChainOfThought isStreaming={reasoningIsStreaming}>
      <ChainOfThoughtHeader>
        {reasoningIsStreaming ? getCoTStepLabel(steps[steps.length - 1]) || 'Thinking...' : 'Chain of Thought'}
      </ChainOfThoughtHeader>
      <ChainOfThoughtContent>
        {steps.map((step, stepIndex) => {
          const stepKey = getCoTStepKey(step, stepIndex);
          const stepStatus = getCoTStepStatus(step);
          const stepProgress = step.type === 'tool' ? stepProgressMap[stepKey] : undefined;
          const inputDescription =
            step.type === 'tool' && step.input != null ? (
              <CoTToolInput toolName={step.toolName} input={step.input} />
            ) : undefined;
          const description =
            inputDescription && stepProgress ? (
              <>
                {inputDescription}
                <div>{stepProgress}</div>
              </>
            ) : (
              (inputDescription ?? stepProgress)
            );
          return (
            <ChainOfThoughtStep
              key={stepKey}
              icon={getCoTStepIcon(step)}
              label={getCoTStepLabel(step)}
              description={description}
              status={stepStatus}
            >
              {step.type === 'reasoning' && step.text && (
                <MessageResponse className="text-muted-foreground">
                  {step.title ? stripLeadingTitle(step.text) : step.text}
                </MessageResponse>
              )}
              {step.type === 'tool' && stepStatus === 'complete' && step.output != null && (
                <>
                  <CoTToolSearchResults toolName={step.toolName} output={step.output} />
                  <div className="text-muted-foreground size-full [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
                    <CoTToolOutput toolName={step.toolName} output={step.output} />
                  </div>
                </>
              )}
            </ChainOfThoughtStep>
          );
        })}
        {!reasoningIsStreaming && (
          <ChainOfThoughtStep key="done" icon={CheckCircle2Icon} label="Done" status="complete" isLast />
        )}
      </ChainOfThoughtContent>
    </ChainOfThought>
  ),
);

ChainOfThoughtBlock.displayName = 'ChainOfThoughtBlock';
